import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import * as echarts from "echarts";
import { dataMap } from "@/constants/distribution";
import materialsDB from "@/constants/materialsDB";
import { rebuildFormula } from "@/config/func";
import { fieldMap } from "@/config/dataMap";
import dayjs from "dayjs";
import towerImage from "@/assets/xdt2.png";
import { hydraulics } from "@/services/common";
import { hyInputs } from "@/Pages/Project/Flow/TaskManagement/TaskDate/ElementPages/Tower/Hydraulics/hyInput";

// ===================== 工具函数 =====================

/** 将图片URL转为base64，确保html2canvas能稳定渲染 */
function imageToBase64(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = reject;
    img.src = url;
  });
}

const fmt = (v: any, decimal = 2) => {
  if (v === undefined || v === null || v === "") return "-";
  const n = Number(v);
  return isNaN(n) ? String(v) : Number(n.toFixed(decimal)).toString();
};

const findMatName = (cas: string) => {
  const m = materialsDB.find((i: any) => i.cas === cas);
  if (m) return `${rebuildFormula(m.Molecular_formula)}(${m.Name_ZH})`;
  return cas;
};

const findMaterial = (mat: any) => {
  const m = materialsDB.find((i: any) => i.cas === mat?.cas);
  if (m) return `${rebuildFormula(m.Molecular_formula)}(${m.Name_ZH})`;
  return mat?.cas || "未知";
};

const convertT = (valK: number, unit: string) => {
  if (unit === "℃") return valK - 273.15;
  if (unit === "K") return valK;
  return valK - 273.15;
};

const convertP = (valPa: number, unit: string) => {
  switch (unit) {
    case "Pa": return valPa;
    case "bar": return valPa / 1e5;
    case "KPa": return valPa / 1000;
    case "MPa": return valPa / 1e6;
    case "atm": return valPa / 101325;
    default: return valPa / 1e6;
  }
};

// ===================== HTML 渲染辅助 =====================

function renderTable(headers: string[], rows: string[][], colWidths?: string[]): string {
  if (rows.length === 0) return "";
  let html = '<table class="report-table"><thead><tr>';
  headers.forEach((h) => {
    html += `<th>${h.replace(/\n/g, "<br>")}</th>`;
  });
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    row.forEach((cell, ci) => {
      html += `<td>${cell}</td>`;
    });
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
}

// ===================== 塔板效果图颜色渐变 =====================

function getGradientColor(value: number): string {
  const clampedValue = Math.max(0, Math.min(1, value));
  const colors = [
    { r: 0, g: 0, b: 255 },   // 蓝色 (0.0)
    { r: 0, g: 255, b: 255 }, // 青色 (0.25)
    { r: 0, g: 255, b: 0 },   // 绿色 (0.5)
    { r: 255, g: 255, b: 0 }, // 黄色 (0.75)
    { r: 255, g: 0, b: 0 },   // 红色 (1.0)
  ];
  const position = clampedValue * 4;
  const index = Math.floor(position);
  const remainder = position - index;
  if (index < 0 || index >= colors.length) return "rgba(128,128,128,0.6)";
  if (remainder === 0) return `rgba(${colors[index].r},${colors[index].g},${colors[index].b},0.6)`;
  const from = colors[index];
  const to = colors[index + 1] || from;
  const r = Math.round(from.r + (to.r - from.r) * remainder);
  const g = Math.round(from.g + (to.g - from.g) * remainder);
  const b = Math.round(from.b + (to.b - from.b) * remainder);
  return `rgba(${r},${g},${b},0.6)`;
}

function buildColors(data: number[]): string[] {
  if (!data || data.length === 0) return [];
  const min = Math.min(...data);
  const max = Math.max(...data);
  if (min === max) return data.map(() => "rgba(0,255,0,0.6)");
  return data.map(v => getGradientColor((v - min) / (max - min)));
}

function renderTowerEffectHTML(data: number[], unit: string, trayH: number, towerImg: string): string {
  if (!data || data.length === 0) return "";
  const colors = buildColors(data);
  const max = Math.max(...data);
  const min = Math.min(...data);
  const maxLabel = max > 0 && max < 1 ? 1 : Math.ceil(max);
  const minLabel = min > 0 && min < 1 ? 0 : Math.floor(min);
  const imgHeight = trayH + 50;
  const trayTop = Math.round(imgHeight * 0.09);  // 塔板色块顶部距离塔体图片顶部的距离

  let html = '<div class="tower-effect-export" style="display:flex;align-items:center;">';
  // 塔体图片 + 塔板色块覆盖层
  html += '<div class="te-tower" style="position:relative;display:inline-block;">';
  html += `<img src="${towerImg}" style="width:60px;height:${imgHeight}px;display:block;" />`;
  html += `<div class="te-trays" style="position:absolute;top:${trayTop}px;left:19px;width:37px;height:${trayH}px;display:flex;flex-direction:column-reverse;">`;
  colors.forEach((color: string, i: number) => {
    html += `<div style="flex:1;background:${color};border:1px solid rgba(0,0,0,0.3);" title="塔板${i + 1}: ${fmt(data[i])}"></div>`;
  });
  html += '</div>';
  html += '</div>'; // te-tower

  // Legend
  html += '<div class="te-legend" style="display:flex;flex-direction:column;align-items:center;margin-left:16px;">';
  html += `<div style="font-size:11px;color:#333;">${fmt(maxLabel)}</div>`;
  html += `<div style="width:12px;height:${trayH - 40}px;background:linear-gradient(to bottom, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff);border:1px solid #999;margin:4px 0;"></div>`;
  html += `<div style="font-size:11px;color:#333;">${fmt(minLabel)}</div>`;
  html += unit ? `<div style="font-size:10px;color:#999;margin-top:4px;">${unit}</div>` : '';
  html += '</div>';
  html += '</div>';
  return html;
}

// 分布数据各模式（T/P/V/L/HV/HL/X/Y/K）
const DIST_MODES = [
  { key: "P", title: "压力" },
  { key: "T", title: "温度" },
  { key: "V", title: "汽相摩尔流量" },
  { key: "L", title: "液相摩尔流量" },
  { key: "HV", title: "汽相焓" },
  { key: "HL", title: "液相焓" },
  { key: "X", title: "液相摩尔分率" },
  { key: "Y", title: "汽相摩尔分率" },
  { key: "K", title: "相平衡常数" },
];

/** 获取模式对应的数据数组（单值或取第一组分） */
function getModeData(elResult: any, modeKey: string, tempUnit: string, pressUnit: string): number[] {
  const raw = elResult[modeKey];
  if (!raw || !Array.isArray(raw)) return [];
  // 数组元素可能是数组（多组分）或单值
  if (raw.length > 0 && Array.isArray(raw[0])) {
    return raw.map((item: any) => {
      const v = Number(item[0] ?? 0);
      if (modeKey === "T") return convertT(v, tempUnit);
      if (modeKey === "P") return convertP(v, pressUnit);
      return v;
    });
  }
  return raw.map((v: any) => {
    const n = Number(v ?? 0);
    if (modeKey === "T") return convertT(n, tempUnit);
    if (modeKey === "P") return convertP(n, pressUnit);
    return n;
  });
}

/** 获取指定组分在某个模式下各塔板的数据 */
function getModeDataForMaterial(elResult: any, modeKey: string, matIndex: number, tempUnit: string, pressUnit: string): number[] {
  const raw = elResult[modeKey];
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map((item: any) => {
    const v = Number(Array.isArray(item) ? (item[matIndex] ?? 0) : (item ?? 0));
    if (modeKey === "T") return convertT(v, tempUnit);
    if (modeKey === "P") return convertP(v, pressUnit);
    return v;
  });
}

/** 获取模式对应的单位标签 */
function getModeUnit(modeKey: string, tempUnit: string, pressUnit: string): string {
  if (modeKey === "T") return tempUnit;
  if (modeKey === "P") return pressUnit;
  return dataMap[modeKey]?.unit || "";
}

// ===================== 水力学图表 =====================

const HYDRO_UNIT = "kg/h";

/** 根据hyResult构建单个塔板的ECharts配置 */
function buildHydroChartOption(hyResultData: any, trayNo: number, unit: string) {
  const { L_values, V_flood, V_leakage, V_max_entrain, L_op, V_op, V_L_ratio_op, L_min, L_max } = hyResultData;

  const LUnitValues: number[] = L_values[unit];
  const VFloodValues: number[] = V_flood[unit];
  const VLeakageValues: number[] = V_leakage[unit];
  const VMaxEntrainValues: number[] = V_max_entrain[unit];
  const LOpValue: number = L_op[unit];
  const VOpValue: number = V_op[unit];
  const ratioValue: number = V_L_ratio_op[unit];
  const LMinValue: number = L_min[unit];
  const LMaxValue: number = L_max[unit];

  const opLineData = [
    [LMinValue, LMinValue * ratioValue],
    [LMaxValue, LMaxValue * ratioValue],
  ];

  const safetyZoneData: number[][] = [];
  for (let i = 0; i < LUnitValues.length; i++) {
    safetyZoneData.push([LUnitValues[i], VLeakageValues[i]]);
  }
  for (let i = LUnitValues.length - 1; i >= 0; i--) {
    safetyZoneData.push([LUnitValues[i], Math.min(VFloodValues[i], VMaxEntrainValues[i])]);
  }

  return {
    animation: false,
    legend: {
      data: ["安全操作区", "液泛线", "漏液线", "最大夹带线", "操作线", "操作点", "液相上限", "液相下限"],
      orient: "vertical" as const,
      right: 8,
      top: 40,
      itemWidth: 20,
      itemHeight: 10,
      textStyle: { fontSize: 10 },
    },
    grid: { left: 120, right: 140, bottom: 50, top: 40 },
    xAxis: {
      type: "value" as const,
      name: `液相流量 L (${unit})`,
      nameLocation: "middle" as const,
      nameGap: 35,
    },
    yAxis: {
      type: "value" as const,
      name: `气相流量 V (${unit})`,
      nameLocation: "middle" as const,
      nameGap: 75,
    },
    series: [
      {
        name: "安全操作区",
        type: "custom",
        renderItem: (_params: any, api: any) => {
          const points = safetyZoneData.map((item: number[]) => api.coord(item));
          return { type: "polygon", shape: { points }, style: { fill: "rgba(16, 185, 129, 0.15)" } };
        },
        data: safetyZoneData,
        z: 0,
        silent: true,
      },
      { name: "液泛线", type: "line", smooth: true, symbol: "none", data: LUnitValues.map((l, i) => [l, VFloodValues[i]]), lineStyle: { width: 3, color: "#f87171" }, z: 3 },
      { name: "漏液线", type: "line", smooth: true, symbol: "none", data: LUnitValues.map((l, i) => [l, VLeakageValues[i]]), lineStyle: { width: 3, color: "#fb923c" }, z: 3 },
      { name: "最大夹带线", type: "line", smooth: true, symbol: "none", data: LUnitValues.map((l, i) => [l, VMaxEntrainValues[i]]), lineStyle: { width: 3, type: "dotted" as const, color: "#322463" }, z: 3 },
      {
        name: "液相上限", type: "line", z: 2,
        markLine: { symbol: ["none", "none"], label: { show: true, formatter: "液相上限", color: "#a855f7", fontSize: 10 }, lineStyle: { type: "dashed" as const, color: "#a855f7", width: 2 }, data: [{ xAxis: LMaxValue }], animation: false },
      },
      {
        name: "液相下限", type: "line", z: 2,
        markLine: { symbol: ["none", "none"], label: { show: true, formatter: "液相下限", color: "#ec4899", fontSize: 10 }, lineStyle: { type: "dashed" as const, color: "#ec4899", width: 2 }, data: [{ xAxis: LMinValue }], animation: false },
      },
      { name: "操作线", type: "line", smooth: false, symbol: "none", data: opLineData, lineStyle: { width: 4, type: "dotted" as const, color: "#60a5fa" }, z: 4 },
      { name: "操作点", type: "scatter", symbolSize: 18, data: [[LOpValue, VOpValue]], itemStyle: { color: "#397429", borderColor: "#eee", borderWidth: 3 }, label: { show: true, formatter: "操作点", position: "top" as const, color: "#000", fontWeight: "bold" as const, fontSize: 14 }, z: 5 },
    ],
  };
}

/** 批量获取所有塔板的水力学计算数据 */
export async function fetchAllTrayHydraulics(elInput: any, elResult: any, trayCount: number): Promise<(any | null)[]> {
  const D = elInput?.D_col ?? elInput?.params?.D_col ?? 2;
  const d_hole = elInput?.d_hole ?? elInput?.params?.d_hole ?? 0.0127;
  const phi_hole = Number((1.02 / ((Math.PI * D * D) / 4)).toFixed(4)) || 0.1;

  const promises = Array.from({ length: trayCount }, async (_, i) => {
    try {
      const input = {
        device_config: {
          ...hyInputs.device_config,
          D,
          phi_hole,
          d_o: d_hole,
        },
        stream_config: {
          ...hyInputs.stream_config,
          L_op: elResult.L_kg_per_h?.[i] || 0,
          V_op: elResult.V_kg_per_h?.[i] || 0,
          rhoL_op: elResult.rho_L?.[i] || 0,
          rhoV_op: elResult.rho_V?.[i] || 0,
          sigma_op: elResult.sigma?.[i] || 0,
        },
      };
      const res = await hydraulics(input, "");
      return res?.data || null;
    } catch {
      return null;
    }
  });

  return Promise.all(promises);
}

/** 渲染水力学逐板ECharts占位canvas */
function renderHydroChartHTML(trayNo: number, hyData: any): string {
  const chartId = `hydrochart-${trayNo}-${Math.random().toString(36).slice(2, 6)}`;
  const dataJson = encodeURIComponent(JSON.stringify(hyData));
  return `
    <div class="report-section">
    <div style="margin:0 0 16px 0;">
      <div style="font-size:12px;font-weight:bold;margin-bottom:4px;">${trayNo + 1}#塔板 水力学操作性能负荷图</div>
      <canvas id="${chartId}" width="660" height="380" style="width:660px;height:380px;" data-hydro-data="${dataJson}" data-hydro-tray="${trayNo}"></canvas>
    </div>
    </div>`;
}

/** 渲染分布图HTML（塔板效果图 + echarts占位canvas，数据存储在data属性中） */
function renderDistChartHTML(modeKey: string, title: string, data: number[], unitLabel: string, towerImg: string): string {
  if (!data || data.length === 0) return "";
  const chartId = `chart-${modeKey}-${Math.random().toString(36).slice(2, 8)}`;
  const dataJson = JSON.stringify(data);
  return `
    <div class="report-section">
    <div class="dist-chart-row" style="display:flex;align-items:flex-start;margin:0 0 24px 0;">
      <div style="width:170px;flex-shrink:0;">
        <div style="font-size:12px;font-weight:bold;margin-bottom:4px;">塔板效果图</div>
        ${renderTowerEffectHTML(data, unitLabel, Math.min(260, Math.max(130, data.length * 5)), towerImg)}
      </div>
      <div style="width:520px;flex-shrink:0;margin-left:10px;">
        <div style="font-size:12px;font-weight:bold;margin-bottom:4px;">${title}分布图</div>
        <canvas id="${chartId}" width="520" height="260" style="width:520px;height:260px;" data-chart-data='${dataJson}' data-chart-unit="${unitLabel}"></canvas>
      </div>
    </div>
    </div>`;
}

export interface ExportOptions {
  /** 选中的元件名称列表，不传则导出全部 */
  selectedElementNames?: string[];
  /** 是否导出水力学计算部分，默认 true */
  includeHydraulics?: boolean;
  /** 关键组分名称列表，不传则导出全部组分 */
  keyComponentNames?: string[];
  /** 流股名 → { fromName: 源元件名, toName: 目标元件名 } */
  streamNodeMap?: Record<string, { fromName: string; toName: string }>;
}

export async function exportReport(
  input: any,
  response: any,
  streamDescMap: Record<string, string>,
  elementDescMap: Record<string, string>,
  tempUnit = "℃",
  pressUnit = "MPa",
  options?: ExportOptions,
) {
  const selectedNames = options?.selectedElementNames;
  const includeHydraulics = options?.includeHydraulics !== false;
  const keyComponentNames = options?.keyComponentNames;
  const streamNodeMap = options?.streamNodeMap;
  // 预加载塔体图片为base64，确保html2canvas稳定渲染
  const towerImgBase64 = await imageToBase64(towerImage);
  let html = "";

  // ========== CSS样式 ==========
  html += `<style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: "Microsoft YaHei", "SimHei", "PingFang SC", "Noto Sans SC", sans-serif; font-size: 12px; color: #333; line-height: 1.6; }
    .report-container { width: 794px; padding: 40px 50px; background: #fff; }
    h1 { font-size: 28px; text-align: center; margin-bottom: 16px; }
    h2 { font-size: 20px; margin: 20px 0 10px; padding-bottom: 4px; border-bottom: 2px solid #1890ff; }
    h3 { font-size: 17px; margin: 14px 0 8px; }
    h4 { font-size: 15px; margin: 10px 0 6px; color: #555; }
    p { margin: 4px 0; }
    .report-table { width: 100%; border-collapse: collapse; margin: 8px 0 12px; font-size: 11px; }
    .report-table th, .report-table td { border: 1px solid #999; padding: 3px 6px; text-align: center; }
    .report-table th { background: #dbe8f8; font-weight: bold; }
    .report-table td { background: #fff; }
    .report-table td:first-child { text-align: left; font-weight: 500; }
    .section-block { margin-bottom: 4px; }
    .meta-line { margin: 2px 0; font-size: 13px; }
    .stream-desc { margin: 2px 0 2px 12px; font-size: 12px; color: #555; }
    .page-break { page-break-before: always; }
    .report-section { page-break-inside: avoid; margin-bottom: 25px; }
    .report-section .report-table { page-break-inside: avoid; margin: 15px 0 20px; }
    .report-section .dist-chart-row { page-break-inside: avoid; margin-bottom: 12px; }
    .report-section .tower-effect-export { page-break-inside: avoid; margin-bottom:20px; }
    .report-section h4 { page-break-after: avoid; }
    .section-spacer { height: 20px; font-size: 0; line-height: 0; }
  </style>`;

  html += '<div class="report-container">';
  html += '<div class="report-section">';

  // ========== 报告头 ==========
  html += `<h1>计算报告</h1>`;
  html += `<p class="meta-line">计算名称：${input?.name || "-"}</p>`;
  html += `<p class="meta-line">完成时间：${dayjs().format("YYYY-MM-DD HH:mm:ss")}</p>`;
  html += '</div>'; // report-section (header)

  // ========== 设备单元循环 ==========
  let elements = response?.elements || [];
  // 如果指定了选中的元件名称，则筛选
  if (selectedNames && selectedNames.length > 0) {
    elements = elements.filter((el: any) => selectedNames.includes(el.name));
  }
  const inputElements = input?.inputs?.elements || [];
  const inputStreams = input?.inputs?.streams || [];
  const resultStreams = response?.streams || [];

  // 收集水力学逐板图表配置（在HTML设置后异步获取并渲染）
  const hydroConfigs: { elIdx: number; elInput: any; elResult: any; trayCount: number }[] = [];

  elements.forEach((el: any, elIdx: number) => {
    const elName = el.name || `设备${elIdx + 1}`;
    const elType = el.type || "";
    const elDesc = elementDescMap?.[elName] || el.description || "";
    const elResult = el.result || {};

    const elInput = inputElements.find((ie: any) => ie.name === elName) || {};
    const elParams = { ...elInput, ...(elInput.params || {}) };

    // ========== 元素标题 ==========
    html += `<div class="section-spacer"></div>`;
    html += `<h2>${elIdx + 1}、${elName}</h2>`;
    if (elDesc) html += `<p>设备描述：${elDesc}</p>`;

    // ========== 计算参数 ==========
    html += `<div class="section-spacer"></div>`;
    html += `<h3>计算参数：</h3>`;

    const isTower = elType === "DistilStrict" || elType === "DistilShortCut";
    const elementStreams: any[] = elInput.streams || elInput.inputStreams || [];
    const feedStreams = elementStreams.filter((s: any) => s.rule !== "D" && s.rule !== "B");
    // 出料流股
    let outputStreams: any[] = [];
    if (isTower) {
      // 塔元件：inputStreams中的D/B（回流/再沸） + outputStreams中的D/B（塔顶/塔底产品）
      const outputFromInput = elementStreams.filter((s: any) => s.rule === "D" || s.rule === "B");
      const outputFromOutput = (elInput.outputStreams || []).filter((s: any) => s.rule === "D" || s.rule === "B");
      const seenNames = new Set<string>();
      for (const s of [...outputFromInput, ...outputFromOutput]) {
        if (!seenNames.has(s.name)) {
          seenNames.add(s.name);
          outputStreams.push(s);
        }
      }
    } else {
      // 非塔元件：outputStreams 中的全部流股作为出料
      outputStreams = elInput.outputStreams || [];
    }

    if (isTower) {
      // 设备参数
      const pLabels: [string, string][] = [
        ["N", "塔板数"], ["P_D", "塔顶压力"], ["pressure_drop", "整塔压降"],
        ["D_col", "塔径"], ["H_col", "总高"], ["shell_length", "壳体长度"],
        ["material", "材质"], ["corrosion_allowance", "腐蚀裕量"],
        ["d_hole", "孔径"], ["surface_roughness", "壁面粗糙度"],
        ["h_w", "堰高"], ["tray_material", "塔板材质"],
      ];
      const equipRows: string[][] = [];
      pLabels.forEach(([key, label]) => {
        if (elParams[key] !== undefined && elParams[key] !== null) {
          const unit = getFieldUnit(key, elInput);
          equipRows.push([label, fmt(elParams[key]) + (unit ? ` ${unit}` : "")]);
        }
      });

      // 塔板效率
      if (elParams.eff !== undefined && elParams.eff !== null) {
        if (Array.isArray(elParams.eff)) {
          elParams.eff.forEach((item: any) => {
            equipRows.push(["塔板效率", `${item.start}-${item.end}:${item.value}`]);
          });
        } else if (typeof elParams.eff === "object") {
          const effText = Object.entries(elParams.eff).map(([k, v]) => `${k}:${v}`).join(" ");
          equipRows.push(["塔板效率", effText]);
        } else {
          equipRows.push(["塔板效率", `1~${elParams.N || "?"}:${elParams.eff}`]);
        }
      }

      if (equipRows.length > 0) {
        html += `<div class="section-spacer"></div>`;
        html += `<h4>设备参数</h4>`;
        html += renderTable(["参数", "数值"], equipRows);
      }

      // 操作参数
      const opRows: string[][] = [];
      if (elParams.top_T !== undefined) opRows.push(["塔顶操作温度", fmt(elParams.top_T) + " ℃"]);
      if (elParams.bottom_T !== undefined) opRows.push(["塔底操作温度", fmt(elParams.bottom_T) + " ℃"]);
      if (elParams.top_P !== undefined) opRows.push(["塔顶操作压力", fmt(elParams.top_P) + " MPa"]);
      if (elParams.bottom_P !== undefined) opRows.push(["塔底操作压力", fmt(elParams.bottom_P) + " MPa"]);
      if (opRows.length > 0) {
        html += `<div class="section-spacer"></div>`;
        html += `<h4>操作参数</h4>`;
        html += renderTable(["参数", "数值"], opRows);
      }
    } else {
      // 非塔元件：使用 fieldMap 统一展示设备参数
      const params = { ...elParams, ...(elParams.params || {}), ...(elInput?.params || {}) };
      const genericRows: string[][] = [];
      for (const [key, config] of Object.entries(fieldMap.elements)) {
        const rawVal = params[key];
        if (rawVal === undefined || rawVal === null) continue;
        const enumMap = (config as any).enum || {};
        const label = (config as any).name || key;
        const unitKey = (config as any).unitKey || "";
        const unit = (config as any).unit || (unitKey ? params[unitKey] : "") || "";
        const displayVal = enumMap[rawVal] ?? rawVal;
        genericRows.push([label, fmt(displayVal) + (unit ? ` ${unit}` : "")]);
      }
      if (genericRows.length > 0) {
        html += `<div class="section-spacer"></div>`;
        html += `<h4>设备参数</h4>`;
        html += renderTable(["参数", "数值"], genericRows);
      }
    }

    // 进料参数
    if (feedStreams.length > 0 && inputStreams.length > 0) {
      const materials = getElementMaterials(elInput, inputStreams, keyComponentNames);
      const feedHeaders = ["流股", ...(isTower ? ["位置"] : []), "流量<br>(kg/h)", "温度<br>(℃)", "压力<br>(MPa)", ...materials.map((m: any) => m.label)];

      let feedPosMap: Record<string, number | string> = {};
      if (isTower) {
        const allInStreams = elInput.inputStreams || elInput.streams || [];
        const nsValues: any[] = elParams.Ns || [];
        let nsIdx = 0;
        for (const s of allInStreams) {
          if (s.rule !== "D" && s.rule !== "B") {
            feedPosMap[s.name] = nsValues[nsIdx] ?? "-";
            nsIdx++;
          }
        }
      }

      const feedRows = feedStreams.map((fs: any) => {
        const st = inputStreams.find((s: any) => s.name === fs.name) || {};
        const params = st.params || {};
        const matRow = materials.map((m: any) => {
          const mat = (st.materials || []).find((sm: any) => sm.cas === m.cas);
          return mat ? fmt(mat.molar_frac, 4) : "-";
        });
        const row = [fs.name, fmt(params.f), fmt(params.t), fmt(params.p), ...matRow];
        if (isTower) {
          row.splice(1, 0, String(feedPosMap[fs.name] ?? fs.position ?? "-"));
        }
        return row;
      });
      html += `<div class="section-spacer"></div>`;
      html += `<h4>进料参数：</h4>`;
      html += renderTable(feedHeaders, feedRows);
    }

    // 出料参数
    if (outputStreams.length > 0 && inputStreams.length > 0) {
      const materials = getElementMaterials(elInput, inputStreams, keyComponentNames);
      const outHeaders = ["流股", ...(isTower ? ["位置"] : []), "流量<br>(kg/h)", "温度<br>(℃)", "压力<br>(MPa)", ...materials.map((m: any) => m.label)];

      let outPosMap: Record<string, number | string> = {};
      if (isTower) {
        const allOutStreams: any[] = elInput.outputStreams || [];
        const noValues: any[] = elParams.No || [];
        for (let i = 0; i < allOutStreams.length; i++) {
          if (allOutStreams[i].rule === "D" || allOutStreams[i].rule === "B") {
            outPosMap[allOutStreams[i].name] = noValues[i] ?? "-";
          }
        }
      }

      const outRows = outputStreams.map((os: any) => {
        const st = inputStreams.find((s: any) => s.name === os.name) || {};
        const params = st.params || {};
        const matRow = materials.map((m: any) => {
          const mat = (st.materials || []).find((sm: any) => sm.cas === m.cas);
          return mat ? fmt(mat.molar_frac, 4) : "-";
        });
        const nameLabel = isTower
          ? os.name + (os.rule === "D" ? "（塔顶）" : os.rule === "B" ? "（塔底）" : "")
          : os.name;
        const row = [nameLabel, fmt(params.f), fmt(params.t), fmt(params.p), ...matRow];
        if (isTower) {
          row.splice(1, 0, String(outPosMap[os.name] ?? os.position ?? "-"));
        }
        return row;
      });
      html += `<div class="section-spacer"></div>`;
      html += `<h4>出料参数：</h4>`;
      html += renderTable(outHeaders, outRows);
    }

    // ========== (1) 设备工艺计算 ==========
    // 包含inputStreams和outputStreams的流股名，确保进出口流股都能匹配到结果
    const elStreamNames = Array.from(new Set([...elementStreams, ...outputStreams].map((s: any) => s.name)));
    const elResultStreams = resultStreams.filter((rs: any) => elStreamNames.includes(rs.name));
    const feedResultNames = feedStreams.map((s: any) => s.name);
    const outputResultNames = outputStreams.map((s: any) => s.name);
    let matNames: string[] = [];
    let filteredMatNames: string[] = [];
    let matFilterIndices: number[] = [];

    if (elResultStreams.length > 0) {
      html += `<div class="section-spacer"></div>`;
      html += isTower ? `<h3>（1）设备工艺计算</h3>` : `<h3>流股数据</h3>`;

      matNames = getResultMaterials(elInput, inputStreams, resultStreams);

      // 按关键组分过滤：记录原始索引，用于后续数组切片
      filteredMatNames = matNames;
      if (keyComponentNames && keyComponentNames.length > 0) {
        const indices: { name: string; idx: number }[] = [];
        matNames.forEach((name, i) => {
          // name 是完整标签如 "CH₃OH(甲醇)"，keyComponentNames 可能只有公式 "CH₃OH"
          const formulaOnly = name.includes("(") ? name.split("(")[0] : name;
          if (keyComponentNames.includes(name) || keyComponentNames.includes(formulaOnly)) {
            indices.push({ name, idx: i });
          }
        });
        filteredMatNames = indices.map((m) => m.name);
        matFilterIndices = indices.map((m) => m.idx);
      }

      // 过滤流股的组分数组，仅保留关键组分
      const filterStreamArrays = (streams: any[]) => {
        if (matFilterIndices.length === 0) return streams;
        return streams.map((st: any) => ({
          ...st,
          molar_frac: matFilterIndices.map((i) => (st.molar_frac || [])[i] ?? 0),
          mass_frac: matFilterIndices.map((i) => (st.mass_frac || [])[i] ?? 0),
        }));
      };

      // 进料流股
      const inStreams = elResultStreams.filter((rs: any) => feedResultNames.includes(rs.name));
      if (inStreams.length > 0) {
        html += `<div class="section-spacer"></div>`;
        html += `<h3>A、进料流股：</h3>`;
        inStreams.forEach((st: any) => {
          const desc = streamDescMap?.[st.name] || "";
          const nodeInfo = streamNodeMap?.[st.name];
          const srcTarget = nodeInfo ? `（源：${nodeInfo.fromName} → 目标：${nodeInfo.toName}）` : "";
          html += `<p class="stream-desc">${st.name}：${desc}${srcTarget}</p>`;
        });

        // 进料流股工艺表
        html += `<div class="section-spacer"></div>`;
        html += '<div class="report-section">';
        html += `<h4>进料流股工艺计算结果</h4>`;
        html += renderStreamPropTable(inStreams, tempUnit, pressUnit);
        html += '</div>';

        // 进料流股组分表
        html += renderStreamCompTables("进料", filterStreamArrays(inStreams), filteredMatNames);
      }

      // 出料流股
      const outStreams = elResultStreams.filter((rs: any) => outputResultNames.includes(rs.name));
      if (outStreams.length > 0) {
        html += `<div class="section-spacer"></div>`;
        html += `<h3>B、出料流股：</h3>`;
        // 塔元件：在流股名后标注塔顶/塔底
        const labeledOutStreams = outStreams.map((st: any) => {
          const rule = outputStreams.find((os: any) => os.name === st.name)?.rule || "";
          const label = isTower
            ? (rule === "D" ? "（塔顶）" : rule === "B" ? "（塔底）" : "")
            : "";
          return { ...st, name: st.name + label };
        });
        outStreams.forEach((st: any, idx: number) => {
          const desc = streamDescMap?.[st.name] || "";
          const nodeInfo = streamNodeMap?.[st.name];
          const srcTarget = nodeInfo ? `（源：${nodeInfo.fromName} → 目标：${nodeInfo.toName}）` : "";
          html += `<p class="stream-desc">${labeledOutStreams[idx].name}：${desc}${srcTarget}</p>`;
        });

        html += `<div class="section-spacer"></div>`;
        html += '<div class="report-section">';
        html += `<h4>出料流股工艺计算结果</h4>`;
        html += renderStreamPropTable(labeledOutStreams, tempUnit, pressUnit);
        html += '</div>';

        html += renderStreamCompTables("出料", filterStreamArrays(labeledOutStreams), filteredMatNames);
      }
    }

    // ========== 塔板数据 ==========
    if (isTower && elResult.T && Array.isArray(elResult.T)) {
      html += `<div class="section-spacer"></div>`;
      html += `<h3>C、塔板数据：</h3>`;
      const trayHeaders = ["序号", `压力(${pressUnit})`, `温度(${tempUnit})`,
        "汽相摩尔流量(kmol/h)", "液相摩尔流量(kmol/h)", "汽相焓(J/mol)",
        "液相焓(J/mol)"];
      const trayRows: string[][] = [];
      const trayCount = elResult.T.length;
      // 辅助：获取塔板i的第一组分值（兼容数组/单值）
      const getFirst = (arr: any, i: number) => {
        const v = arr?.[i];
        return (v !== undefined && v !== null) ? Number(Array.isArray(v) ? v[0] : v) : 0;
      };
      for (let i = 0; i < trayCount; i++) {
        trayRows.push([
          `塔板${i + 1}`,
          fmt(convertP(elResult.P?.[i], pressUnit)),
          fmt(convertT(elResult.T[i], tempUnit)),
          fmt(getFirst(elResult.V, i) / 1000),
          fmt(getFirst(elResult.L, i) / 1000),
          fmt(getFirst(elResult.HV, i)),
          fmt(getFirst(elResult.HL, i)),
        ]);
      }
      html += '<div class="report-section">';
      html += renderTable(trayHeaders, trayRows);
      html += '</div>';

      // ========== 分布数据图（塔板效果图 + echarts图，左右结构） ==========
      const distModesWithData: { key: string; title: string; data: number[]; unit: string }[] = [];
      const perMatModes = ["X", "Y", "K"]; // 需要按组分分别展示的模式

      DIST_MODES.forEach(({ key, title }) => {
        if (perMatModes.includes(key)) {
          // X/Y/K模式：按每种组分分别生成图表
          if (filteredMatNames.length > 0) {
            filteredMatNames.forEach((matName, filteredIdx) => {
              const matIdx = matFilterIndices.length > 0 ? matFilterIndices[filteredIdx] : filteredIdx;
              const data = getModeDataForMaterial(elResult, key, matIdx, tempUnit, pressUnit);
              if (data.length > 0) {
                const chartKey = `${key}-${matIdx}`;
                distModesWithData.push({
                  key: chartKey,
                  title: `${matName} ${title}`,
                  data,
                  unit: getModeUnit(key, tempUnit, pressUnit),
                });
              }
            });
          }
        } else {
          // 非X/Y/K模式：一个模式一张图
          const data = getModeData(elResult, key, tempUnit, pressUnit);
          if (data.length > 0) {
            distModesWithData.push({ key, title, data, unit: getModeUnit(key, tempUnit, pressUnit) });
          }
        }
      });

      if (distModesWithData.length > 0) {
        html += `<div class="section-spacer"></div>`;
        html += `<h4>分布数据图：</h4>`;
        distModesWithData.forEach(({ key, title, data, unit }) => {
          html += renderDistChartHTML(key, title, data, unit, towerImgBase64);
        });
      }

      // ========== 水力学计算 ==========
      if (includeHydraulics) {
      const hydraulicsData = elResult.hydraulics || {};
      const hydroQV = hydraulicsData.Q_V && Array.isArray(hydraulicsData.Q_V) ? hydraulicsData.Q_V.map((v: any) => Number(v)) : null;
      const hydroQL = hydraulicsData.Q_L && Array.isArray(hydraulicsData.Q_L) ? hydraulicsData.Q_L.map((v: any) => Number(v)) : null;
      const hydroDP = hydraulicsData.delta_P_tray && Array.isArray(hydraulicsData.delta_P_tray)
        ? hydraulicsData.delta_P_tray.map((v: any) => Number(v)) : null;

      if (hydroQV || hydroQL || hydroDP) {
        html += `<div class="section-spacer"></div>`;
        html += `<h3>（2）水力学计算</h3>`;

        const hydroItems: { key: string; title: string; data: number[]; unit: string }[] = [];
        if (hydroQV && hydroQV.length > 0) hydroItems.push({ key: "Q_V", title: "塔板气相体积流量分布", data: hydroQV, unit: "m³/h" });
        if (hydroQL && hydroQL.length > 0) hydroItems.push({ key: "Q_L", title: "塔板液相体积流量分布", data: hydroQL, unit: "m³/h" });
        if (hydroDP && hydroDP.length > 0) hydroItems.push({ key: "delta_P", title: "塔板压降分布", data: hydroDP, unit: "Pa" });

        if (hydroItems.length > 0) {
          html += '<div class="report-section">';
          html += '<div style="display:flex;flex-wrap:wrap;gap:20px;justify-content:center;">';
          hydroItems.forEach(({ key, title, data, unit }) => {
            html += '<div style="width:200px;">';
            html += `<div style="font-size:12px;font-weight:bold;margin-bottom:4px;text-align:center;">${title}</div>`;
            html += renderTowerEffectHTML(data, unit, Math.min(260, Math.max(130, data.length * 5)), towerImgBase64);
            html += '</div>';
          });
          html += '</div>';
          html += '</div>';
        }

        // 逐板水力学数据表
        if (hydroDP && hydroDP.length > 0) {
          const hydroTrayHeaders = ["塔板", "气相体积流量<br>(m³/h)", "液相体积流量<br>(m³/h)", "压降<br>(Pa)"];
          const hydroTrayRows: string[][] = [];
          const trayCount = hydroDP.length;
          for (let i = 0; i < trayCount; i++) {
            hydroTrayRows.push([
              `塔板${i + 1}`,
              fmt(hydroQV?.[i]),
              fmt(hydroQL?.[i]),
              fmt(hydroDP[i]),
            ]);
          }
          html += `<div class="section-spacer"></div>`;
          html += `<h4>逐板水力学数据</h4>`;
          html += '<div class="report-section">';
          html += renderTable(hydroTrayHeaders, hydroTrayRows);
          html += '</div>';
        }

        // 逐板水力学性能负荷图
        html += `<div class="section-spacer"></div>`;
        html += `<h4>逐板水力学性能负荷图</h4>`;
        html += `<div class="hydro-charts-placeholder" data-el-idx="${elIdx}"></div>`;
        hydroConfigs.push({ elIdx, elInput, elResult, trayCount: hydroDP?.length || 0 });
      }
      } // includeHydraulics
    }
  });

  html += "</div>";

  // ===================== HTML→Canvas→PDF =====================
  // 渲染到可见区域（echarts需要可见DOM）
  const container = document.createElement("div");
  // container.style.cssText = "position:fixed;top:0;left:0;width:794px;z-index:99999;background:#fff;";
  container.style.cssText = "position:fixed;top:0;left:-9999px;width:794px;z-index:99999;background:#fff;";
  container.innerHTML = html;
  document.body.appendChild(container);

  try {
    // ========== 异步获取水力学逐板数据并插入图表HTML ==========
    for (const hc of hydroConfigs) {
      const placeholder = container.querySelector(`.hydro-charts-placeholder[data-el-idx="${hc.elIdx}"]`);
      if (!placeholder || hc.trayCount === 0) continue;

      const allTrayData = await fetchAllTrayHydraulics(hc.elInput, hc.elResult, hc.trayCount);
      let hydroChartsHTML = "";
      allTrayData.forEach((data, i) => {
        if (data) {
          hydroChartsHTML += renderHydroChartHTML(i, data);
        }
      });
      if (hydroChartsHTML) {
        placeholder.innerHTML = hydroChartsHTML;
        // 将动态插入的report-section提升为report-container的直接子节点，避免嵌套
        const innerSections = Array.from(placeholder.querySelectorAll(".report-section"));
        innerSections.forEach(sec => placeholder.parentNode?.insertBefore(sec, placeholder));
        placeholder.remove();
      }
    }

    // 初始化echarts图表并转换为图片
    const chartCanvases = Array.from(container.querySelectorAll("canvas[id^='chart-']"));
    for (const canvas of chartCanvases) {
      const el = canvas as HTMLCanvasElement;
      //像素点位
      // const instance = echarts.init(el, null, { devicePixelRatio: 2 });
      const instance = echarts.init(el, null, { devicePixelRatio: 1 });
      const modeKey = el.id.replace(/^chart-([A-Z]+)-.*$/, "$1");
      const modeInfo = dataMap[modeKey] || {};
      const chartDataStr = el.getAttribute("data-chart-data") || "[]";
      const chartUnit = el.getAttribute("data-chart-unit") || modeInfo.unit || "";
      let chartData: number[] = [];
      try { chartData = JSON.parse(chartDataStr); } catch (e) { /* ignore */ }
      const trayCount = chartData.length;

      instance.setOption({
        animation: false,
        grid: { left: 110, right: 12, top: 12, bottom: 25 },
        xAxis: {
          type: "category" as const,
          data: Array.from({ length: trayCount }, (_, i) => trayCount > 30 ? 
            (i % 5 === 0 ? `${i + 1}` : "") : `${i + 1}`),
          name: "塔板",
          nameLocation: "middle" as const,
          nameGap: 25,
        },
        yAxis: {
          type: "value" as const,
          scale: true,
          name: chartUnit,
          nameLocation: "middle" as const,
          nameGap: 95,
          axisLabel: {
            formatter: (value: number) => {
              if (value === 0) return "0";
              const absValue = Math.abs(value);
              if (absValue >= 1000) return String(Math.round(value));
              if (absValue >= 100) return value.toFixed(1);
              if (absValue >= 10) return value.toFixed(2);
              if (absValue >= 1) return value.toFixed(3);
              if (absValue >= 0.000001) return value.toFixed(6).replace(/\.?0+$/, "");
              return value.toFixed(12).replace(/\.?0+$/, "");
            },
          },
        },
        series: [{
          type: "line",
          symbol: "none",
          lineStyle: { color: "#1890ff", width: 2 },
          data: chartData,
        }],
      });

      // animation:false 时图表即时渲染，短暂等待后转为图片
      await new Promise(resolve => setTimeout(resolve, 50));
      const dataUrl = instance.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#fff" });
      const img = document.createElement("img");
      img.src = dataUrl;
      img.style.width = "520px";
      img.style.height = "260px";
      el.parentNode?.replaceChild(img, el);
      instance.dispose();
    }

    // ========== 初始化水力学逐板ECharts ==========
    const hydroCanvases = Array.from(container.querySelectorAll("canvas[id^='hydrochart-']"));
    for (const canvas of hydroCanvases) {
      const el = canvas as HTMLCanvasElement;
      //像素点位
      // const instance = echarts.init(el, null, { devicePixelRatio: 2 });
      const instance = echarts.init(el, null, { devicePixelRatio: 1 });
      const dataStr = el.getAttribute("data-hydro-data") || "";
      const trayNo = parseInt(el.getAttribute("data-hydro-tray") || "0", 10);
      try {
        const hyData = JSON.parse(decodeURIComponent(dataStr));
        const option = buildHydroChartOption(hyData, trayNo, HYDRO_UNIT);
        instance.setOption(option);
      } catch { /* ignore */ }

      await new Promise(resolve => setTimeout(resolve, 50));
      const dataUrl = instance.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#fff" });
      const img = document.createElement("img");
      img.src = dataUrl;
      img.style.width = "660px";
      img.style.height = "380px";
      el.parentNode?.replaceChild(img, el);
      instance.dispose();
    }

    // 短暂等待确保所有DOM更新完成
    await new Promise(resolve => setTimeout(resolve, 100));

    // ========== 逐Section渲染，避免分页切断内容 ==========
    const pdf = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });
    const pdfW = pdf.internal.pageSize.getWidth();
    const pdfH = pdf.internal.pageSize.getHeight();
    const pageMargin = 10; // 页边距

    // 按顺序遍历容器子节点：report-section保持完整不拆分，其他节点单独捕获
    const children = Array.from(container.querySelector(".report-container")?.children || []) as HTMLElement[];
    let currentPageY = 0;
    let isFirstBlock = true;

    for (const block of children) {
      if (!block || block.offsetHeight === 0) continue;
      const isSection = block.classList.contains("report-section");

      const secCanvas = await html2canvas(block, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const secImgW = pdfW - pageMargin * 2;
      const secImgH = (secCanvas.height * secImgW) / secCanvas.width;
      const maxContentH = pdfH - pageMargin * 2;

      // 如果当前页放不下，且不是第一块，另起一页
      if (!isFirstBlock && currentPageY + secImgH > maxContentH) {
        pdf.addPage();
        currentPageY = 0;
      }
      isFirstBlock = false;

      // 分段内容超过一页高度时，分页切割
      let secRemainH = secImgH;
      let secSrcY = 0;

      while (secRemainH > 0) {
        const availableH = maxContentH - currentPageY;

        if (availableH <= 0) {
          pdf.addPage();
          currentPageY = 0;
          continue;
        }

        const sliceImgH = Math.min(secRemainH, availableH);
        const sliceSrcH = (sliceImgH / secImgH) * secCanvas.height;

        if (currentPageY > 0 && currentPageY + sliceImgH > maxContentH) {
          pdf.addPage();
          currentPageY = 0;
          continue;
        }

        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = secCanvas.width;
        sliceCanvas.height = sliceSrcH;
        const sliceCtx = sliceCanvas.getContext("2d")!;
        sliceCtx.drawImage(secCanvas, 0, secSrcY, secCanvas.width, sliceSrcH, 0, 0, secCanvas.width, sliceSrcH);
//像素点位
        // const sliceData = sliceCanvas.toDataURL("image/png");
        // pdf.addImage(sliceData, "PNG", pageMargin, pageMargin + currentPageY, secImgW, sliceImgH);
        const sliceData = sliceCanvas.toDataURL("image/jpeg", 0.85);
        pdf.addImage(sliceData, "JPEG", pageMargin, pageMargin + currentPageY, secImgW, sliceImgH);

        currentPageY += sliceImgH;
        secRemainH -= sliceImgH;
        secSrcY += sliceSrcH;
      }
    }

    const fileName = `${input?.name || "计算报告"}_${dayjs().format("YYYYMMDD_HHmmss")}.pdf`;
    pdf.save(fileName);
  } finally {
    if (container.parentNode) document.body.removeChild(container);
  }
}

// ===================== 辅助函数 =====================

function getFieldUnit(key: string, elInput: any): string {
  const unitMap: Record<string, string> = {
    P_D: "MPa", pressure_drop: "MPa", D_col: "m", H_col: "m",
    shell_length: "m", d_hole: "m", h_w: "m", corrosion_allowance: "mm",
  };
  if (unitMap[key]) return unitMap[key];
  const unitKeys: Record<string, string> = { P_D: "p_unit", pressure_drop: "p_unit", t: "t_unit", f: "f_unit" };
  if (unitKeys[key] && elInput?.[unitKeys[key]]) return elInput[unitKeys[key]];
  return "";
}

function getElementMaterials(elInput: any, inputStreams: any[], keyComponentNames?: string[]) {
  const elementStreams: any[] = elInput.streams || elInput.inputStreams || [];
  const names = elementStreams.map((s: any) => s.name);
  for (const name of names) {
    const st = inputStreams.find((s: any) => s.name === name);
    if (st?.materials?.length) {
      let mats = st.materials.map((m: any) => ({ cas: m.cas, label: findMatName(m.cas) }));
      if (keyComponentNames && keyComponentNames.length > 0) {
        // keyComponentNames 可能只有公式（如 "CH₃OH"），需要同时匹配完整标签（如 "CH₃OH(甲醇)"）和公式部分
        mats = mats.filter((m: any) => {
          const formulaOnly = m.label.includes("(") ? m.label.split("(")[0] : m.label;
          return keyComponentNames.includes(m.label) || keyComponentNames.includes(formulaOnly);
        });
      }
      return mats;
    }
  }
  return [];
}

function getResultMaterials(elInput: any, inputStreams: any[], resultStreams: any[]) {
  const elementStreams: any[] = elInput.streams || elInput.inputStreams || [];
  const names = elementStreams.map((s: any) => s.name);
  for (const name of names) {
    const st = inputStreams.find((s: any) => s.name === name);
    if (st?.materials?.length) return st.materials.map((m: any) => findMaterial(m));
  }
  for (const rs of resultStreams) {
    if (rs.molar_frac?.length) return rs.molar_frac.map((_: any, i: number) => `组分${i + 1}`);
  }
  return [];
}

function renderStreamPropTable(streams: any[], tempUnit: string, pressUnit: string): string {
  const headers = ["流股", "相态", "气化率", `温度<br>(${tempUnit})`, `压力<br>(${pressUnit})`,
    "摩尔流量<br>(kmol/h)", "质量流量<br>(kg/h)", "焓<br>(J/mol)",
    "气相体积流量<br>(Nm³/h)", "液相体积流量<br>(m³/h)", "液相体积流量<br>(L/h)"];
  const rows = streams.map((st: any) => [
    st.name,
    st.phase === "V" ? "气相" : st.phase === "L" ? "液相" : st.phase || "-",
    fmt(st.vf || st.vapor_frac, 2),
    fmt(convertT(st.T_K || st.temperature, tempUnit)),
    fmt(convertP(st.P_Pa || st.pressure, pressUnit)),
    fmt((st.f_mol_per_h || st.molar_flow || 0) / 1000),
    fmt(st.f_kg_per_h || st.mass_flow),
    fmt(st.H || st.enthalpy),
    fmt(st.f_nm3_per_h),
    fmt(st.f_m3_per_h),
    fmt(st.f_l_per_h),
  ]);
  return renderTable(headers, rows);
}

function renderStreamCompTables(label: string, streams: any[], matNames: string[]): string {
  if (matNames.length === 0) return "";

  let html = "";

  // 摩尔分率
  html += `<div class="section-spacer"></div>`;
  html += '<div class="report-section">';
  html += `<h4>${label}流股组分数据：摩尔分率</h4>`;
  const mfHeaders = ["流股", ...matNames.map((n: any) => String(n))];
  const mfRows = streams.map((st: any) => {
    const mf = st.molar_frac || [];
    return [st.name, ...matNames.map((_: any, i: number) => fmt(mf[i], 6))];
  });
  html += renderTable(mfHeaders, mfRows);
  html += '</div>';

  // 质量分率
  html += `<div class="section-spacer"></div>`;
  html += '<div class="report-section">';
  html += `<h4>${label}流股组分数据：质量分率</h4>`;
  const massFracHeaders = ["流股", ...matNames.map((n: any) => `${n}`)];
  const massFracRows = streams.map((st: any) => {
    const massFrac = st.mass_frac || [];
    return [st.name, ...matNames.map((_: any, i: number) => fmt(massFrac[i], 6))];
  });
  html += renderTable(massFracHeaders, massFracRows);
  html += '</div>';

  // 摩尔流量
  html += `<div class="section-spacer"></div>`;
  html += '<div class="report-section">';
  html += `<h4>${label}流股组分数据：摩尔流量（kmol/h）</h4>`;
  const mfFlowHeaders = ["流股", ...matNames.map((n: any) => `${n}`)];
  const mfFlowRows = streams.map((st: any) => {
    const mf = st.molar_frac || [];
    const totalFlow = (st.f_mol_per_h || 0) / 1000;
    return [st.name, ...matNames.map((_: any, i: number) => fmt(mf[i] * totalFlow, 6))];
  });
  html += renderTable(mfFlowHeaders, mfFlowRows);
  html += '</div>';

  // 质量流量
  html += `<div class="section-spacer"></div>`;
  html += '<div class="report-section">';
  html += `<h4>${label}流股组分数据：质量流量（kg/h）</h4>`;
  const massHeaders = ["流股", ...matNames.map((n: any) => `${n}`)];
  const massRows = streams.map((st: any) => {
    const massFrac = st.mass_frac || [];
    const totalMass = st.f_kg_per_h || 0;
    return [st.name, ...matNames.map((_: any, i: number) => fmt(massFrac[i] * totalMass, 6))];
  });
  html += renderTable(massHeaders, massRows);
  html += '</div>';

  // 质量浓度
  html += `<div class="section-spacer"></div>`;
  html += '<div class="report-section">';
  html += `<h4>${label}流股组分质量浓度（单位：mg/Nm³）</h4>`;
  const concHeaders = ["流股", ...matNames.map((n: any) => `${n}`)];
  const concRows = streams.map((st: any) => {
    const massFrac = st.mass_frac || [];
    const totalMass = st.f_kg_per_h || 0;
    const volFlow = st.f_nm3_per_h || st.f_m3_per_h || 1;
    return [st.name, ...matNames.map((_: any, i: number) => fmt((massFrac[i] * totalMass * 1e6) / volFlow, 6))];
  });
  html += renderTable(concHeaders, concRows);
  html += '</div>';

  return html;
}
